/**
 * @fileoverview Input/output schemas for structural-suggestions-v1.
 *
 * This agent receives a divergence report and existing storyline structure,
 * then proposes concrete structural changes: beat moves, additions, removals,
 * priority changes, and new storylines.
 *
 * Suggestions are proposals for author review — never auto-applied.
 *
 * Output is flat (3 levels max) for Gemini API compatibility.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 *
 * Receives:
 * - divergenceReport: Output from divergence-report-v2 (the diagnostic step)
 * - existingStorylines: Current storyline structure with beats, questions, phases
 * - chapterSummaries: What was actually written
 * - storyContext: Broader narrative context
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.existingStorylines.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterSummaries.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.projectedWordCount.schema.shape,
  divergenceReport: z.any().describe(
    'The output from divergence-report-v2: summary, plotDivergences, storylineDivergences, '
    + 'characterDivergences, threadDivergences.'
  ),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * A suggestion to move an existing beat to a different structural phase.
 */
const MoveBeatSuggestionSchema = z.object({
  type: z.literal('move_beat'),
  beatId: z.string().describe('The ID of the beat to move.'),
  storylineName: z.string().describe('The name of the storyline this beat belongs to.'),
  storylineIndex: z.number().describe('Index of the storyline in existingStorylines (0-based).'),
  currentGlobalPhase: z.string().describe('The beat\'s current globalPhase.'),
  currentStorylinePhase: z.string().describe('The beat\'s current storylinePhase.'),
  newGlobalPhase: z.string().describe('The proposed new globalPhase for the beat.'),
  newStorylinePhase: z.string().describe('The proposed new storylinePhase for the beat.'),
  rationale: z.string().describe(
    'Why this move is suggested, referencing specific chapter evidence (2-3 sentences).'
  ),
  significance: z.enum(['moderate', 'major']).describe('How impactful this suggestion is.'),
});

/**
 * A suggestion to add a new beat to an existing storyline.
 */
const AddBeatSuggestionSchema = z.object({
  type: z.literal('add_beat'),
  storylineName: z.string().describe('The name of the storyline to add the beat to.'),
  storylineIndex: z.number().describe('Index of the storyline in existingStorylines (0-based).'),
  globalPhase: z.string().describe('The proposed globalPhase for the new beat.'),
  storylinePhase: z.string().describe('The proposed storylinePhase for the new beat.'),
  plotEvent: z.string().describe(
    'The proposed plotEvent text for the new beat. Written in present tense, '
    + 'specific and concrete, referencing actual events from the chapters.'
  ),
  narrativeState: z.string().optional().describe('Optional proposed narrativeState for the new beat.'),
  goalProgress: z.number().min(-1).max(1).describe(
    'Proposed goal progress for the new beat (-1 to +1).'
  ),
  rationale: z.string().describe(
    'Why this beat should be added, referencing specific chapter evidence (2-3 sentences).'
  ),
  significance: z.enum(['moderate', 'major']).describe('How impactful this suggestion is.'),
});

/**
 * A suggestion to remove an existing beat that describes events that won't happen.
 */
const RemoveBeatSuggestionSchema = z.object({
  type: z.literal('remove_beat'),
  beatId: z.string().describe('The ID of the beat to remove.'),
  storylineName: z.string().describe('The name of the storyline this beat belongs to.'),
  storylineIndex: z.number().describe('Index of the storyline in existingStorylines (0-based).'),
  currentPlotEvent: z.string().describe('The current plotEvent text of the beat being removed (for author reference).'),
  rationale: z.string().describe(
    'Why this beat should be removed — what evidence shows it won\'t happen (2-3 sentences).'
  ),
  significance: z.enum(['moderate', 'major']).describe('How impactful this suggestion is.'),
});

/**
 * A suggestion to change a storyline's priority level (A/B/C).
 */
const ChangePrioritySuggestionSchema = z.object({
  type: z.literal('change_priority'),
  storylineName: z.string().describe('The name of the storyline whose priority should change.'),
  storylineIndex: z.number().describe('Index of the storyline in existingStorylines (0-based).'),
  currentPriority: z.enum(['A', 'B', 'C']).describe('The storyline\'s current priority.'),
  newPriority: z.enum(['A', 'B', 'C']).describe('The proposed new priority.'),
  rationale: z.string().describe(
    'Why the priority should change, referencing prominence in the written chapters (2-3 sentences).'
  ),
  significance: z.enum(['moderate', 'major']).describe('How impactful this suggestion is.'),
});

/**
 * A suggestion to add an entirely new storyline with beats and questions.
 */
const AddStorylineSuggestionSchema = z.object({
  type: z.literal('add_storyline'),
  name: z.string().describe('Proposed name for the new storyline (2-5 words).'),
  priority: z.enum(['A', 'B', 'C']).describe('Proposed priority for the new storyline.'),
  summary: z.string().describe('Summary of the new storyline (1-3 sentences).'),
  beats: z.array(z.object({
    globalPhase: z.string().describe('The globalPhase for this beat.'),
    storylinePhase: z.string().describe('The storylinePhase for this beat.'),
    plotEvent: z.string().describe('The plotEvent text for this beat.'),
    narrativeState: z.string().optional().describe('Optional narrativeState.'),
    goalProgress: z.number().min(-1).max(1).describe('Goal progress (-1 to +1).'),
  })).describe('Proposed beats for the new storyline.'),
  storyQuestions: z.array(z.object({
    question: z.string().describe('The story question text.'),
    status: z.enum(['open', 'answered']).describe('Current status based on what has been written.'),
  })).describe('Proposed story questions for the new storyline.'),
  rationale: z.string().describe(
    'Why this storyline should be added, referencing sustained presence across chapters (2-3 sentences).'
  ),
  significance: z.enum(['moderate', 'major']).describe('How impactful this suggestion is.'),
});

/**
 * Union of all suggestion types.
 * Using a discriminated union on `type` for clean downstream handling.
 */
const StructuralSuggestionSchema = z.discriminatedUnion('type', [
  MoveBeatSuggestionSchema,
  AddBeatSuggestionSchema,
  RemoveBeatSuggestionSchema,
  ChangePrioritySuggestionSchema,
  AddStorylineSuggestionSchema,
]);

/**
 * Output schema for structural-suggestions-v1.
 */
export const OutputSchema = z.object({
  commentary: z.string().describe(
    'A brief editorial summary of the structural state and the suggestions being made (3-5 sentences). '
    + 'Frame suggestions as options, not mandates.'
  ),

  suggestions: z.array(StructuralSuggestionSchema).describe(
    'Ordered list of structural suggestions, from most impactful to least. '
    + 'Each suggestion is a concrete, actionable proposal the author can accept or reject independently. '
    + 'Only include suggestions with clear evidence from the written chapters. '
    + 'Return an empty array if no structural changes are warranted.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
export type StructuralSuggestion = z.infer<typeof StructuralSuggestionSchema>;
export type MoveBeatSuggestion = z.infer<typeof MoveBeatSuggestionSchema>;
export type AddBeatSuggestion = z.infer<typeof AddBeatSuggestionSchema>;
export type RemoveBeatSuggestion = z.infer<typeof RemoveBeatSuggestionSchema>;
export type ChangePrioritySuggestion = z.infer<typeof ChangePrioritySuggestionSchema>;
export type AddStorylineSuggestion = z.infer<typeof AddStorylineSuggestionSchema>;
